import { useEffect, useState } from "react";
import { Checkbox, Dropdown, Input } from "src/common/components/Input";
import TextArea from "src/common/components/Input/TextArea";
import {
  IBlockadeLabsModel,
  addBlockadeImageToDatabase,
  generateBlockadeLabsImagePreview,
  getBlockadeLabsPreview,
  mediaSelector,
  resetBlockadeLabsImagePreview,
  setCurrentMediaGallery,
} from "../state/gallerySlice";
import { useAppDispatch } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import { IBlockadeLabsPreview } from "../../gallery/state/gallerySlice";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import Spinner from "src/common/components/Loader/Spinner";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { fromUrlReturnMD5AndSize } from "../state/uploadUtil";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { FETCH_AI_MODELS_FROM_BLOCKADE_LABS } from "./GenerateMediaPanel";
import { MediaGenerationInfoParagraph } from "./MediaGenerationInfoParagraph";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import BlockadeImageToImage from "./BlockadeImageToImage";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { IMedia } from "src/model/unityObject";

export const GENERATE_BLOCKADE_IMAGE_COMPONENT = "generateBlockadeImageComponent";

export interface IGenerateBlockadeImagePreferences {
  defaultWorkflow: string;
}

export const GenerateBlockadeLabsImage = ({
  models = [],
  closeSliderAnimation,
}: {
  models: IBlockadeLabsModel[];
  closeSliderAnimation(): any;
}) => {
  const currentUser = useSelector(getCurrentUser);
  const medias = useSelector(mediaSelector);

  const userPreferences = getUserPreferences({
    currentUser,
  }) as unknown as IGenerateBlockadeImagePreferences;
  const [img2Img, setImg2Img] = useState(userPreferences?.defaultWorkflow || "exNihilo");
  const [imageSource, setImageSource] = useState<File | string | null>(null);
  const [influenceValue, setInfluenceValue] = useState(0.8); // Default 80% resemblance (0.8 in RangeSelector format)

  const toggleImg2Img = () => {
    setImg2Img(img2Img === "img2Img" ? "exNihilo" : "img2Img");
  };

  useEffect(() => {
    const defaultModel = [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    };
    setDefaultModel(defaultModel);
    setSelectedModel(defaultModel);
  }, [models]);

  const [defaultModel, setDefaultModel] = useState(
    [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    },
  );
  const [selectedModel, setSelectedModel] = useState(
    [...models].find((m) => m.id === 112) || {
      id: 112,
      name: "M3 Advanced (photo/render)",
      "max-char": 2000,
      image:
        "https://images.blockadelabs.com/images/skybox/O0wGV3yQ5aVDytx1neT8BqPLnXyJuX7yR2K6Oy50.webp",
    },
  );
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerateRequestBeenSent, setGenerateRequestHasBeenSent] = useState(false);
  const [tagEditorKey, setTagEditorKey] = useState(0);
  const preview = useSelector(getBlockadeLabsPreview) as IBlockadeLabsPreview;
  const [isSaving, setIsSaving] = useState(false);
  const [nameUnique, setNameUnique] = useState(true);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { register, handleSubmit } = useForm<{ name: string }>({
    defaultValues: {
      name: "",
    },
  });

  const handleImageSelect = (value: File | string) => {
    setImageSource(value);
  };

  const cancelImageSelection = () => {
    setImageSource(null);
  };

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const handleModelChange = (event) => {
    const newModel = models.find((model) => model.id === parseInt(event, 10));
    setSelectedModel(newModel || defaultModel);
  };

  const generatePreview = async () => {
    if (isGenerating || hasGenerateRequestBeenSent) return;

    setIsGenerating(true);
    setGenerateRequestHasBeenSent(true);

    let imagePrompt;

    if (imageSource) {
      if (typeof imageSource === "string") {
        // If the image source is an S3 URL, use it as is.
        imagePrompt = imageSource;
      } else if (imageSource instanceof File) {
        // Convert local file to base64.
        imagePrompt = await new Promise<string | undefined>((resolve, reject) => {
          const fileReader = new FileReader();
          fileReader.onload = () => resolve(fileReader.result as string);
          fileReader.onerror = () => reject("Error reading file as base64");
          fileReader.readAsDataURL(imageSource);
        }).catch((error) => {
          console.error(error);
          setIsGenerating(false);
          setGenerateRequestHasBeenSent(false);
          return;
        });
      }
    }

    // Convert RangeSelector value (0.01-1.0) to Blockade API init_strength (0.9-0.11, inverted)
    // Higher value = higher influence = lower init_strength value
    // Clamp to valid range to avoid floating-point precision issues
    const initStrength = imagePrompt
      ? Math.max(0.11, Math.min(0.9, 0.9 - influenceValue * 0.79))
      : undefined;

    const payload = {
      modelId: selectedModel.id,
      prompt,
      imagePrompt, // Include the optional imagePrompt here.
      initStrength, // Include the influence strength
      componentId: GENERATE_BLOCKADE_IMAGE_COMPONENT,
    };

    dispatch(generateBlockadeLabsImagePreview(payload));
  };

  useEffect(() => {
    if (isGenerating) {
      if (preview && preview?.thumbnailUrl) {
        setIsGenerating(false);
      }
    }
    if (preview && preview?.faultyImageId) {
      console.error("Blockade generation error received:", preview);
      cancelPreview();
      setSelectedModel(defaultModel);

      // Display the specific error message from Blockade Labs, or fall back to generic message
      const errorMsg = preview.errorMessage
        ? `${t("error.blockadeLabsApiError")}: ${preview.errorMessage}`
        : t("error.blockadeLabsApiError");

      alert(errorMsg);
    }
  }, [preview]);

  useEffect(() => {
    cancelPreview();
  }, []);

  const saveImage = async (formData) => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const { name } = formData;

      const nameNotAvailable = medias.find((m: IMedia) => m.name === name);

      if (nameNotAvailable) {
        setNameUnique(false);
        setIsSaving(false);
        return;
      } else {
        setNameUnique(true);
      }

      const md5AndSize = await fromUrlReturnMD5AndSize(preview.imageUrl);
      const result = await addBlockadeImageToDatabase({
        name: name,
        imageUrl: preview.imageUrl,
        thumbnailUrl: preview.thumbnailUrl,
        fileSizeInByte: md5AndSize.size,
        fileName: preview.imageUrl,
        MD5: md5AndSize.MD5,
        prompt,
        dispatch,
      });

      dispatch(setCurrentMediaGallery(result.id));
      cancelPreview();
      dispatch(
        setAlert({
          type: "success",
          msg: t("pages.gallery.generateMediaPanel.uploadBlockadeImageSuccess"),
        }),
      );
      closeSliderAnimation();
    } catch (error) {
      console.error("Error saving image:", error);
      dispatch(
        setAlert({
          type: "error",
          msg: t("pages.gallery.generateMediaPanel.uploadBlockadeImageFailure"),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const cancelPreview = () => {
    dispatch(resetBlockadeLabsImagePreview());
    setIsGenerating(false);
    setTagEditorKey(tagEditorKey + 1);
    setGenerateRequestHasBeenSent(false);
    setNameUnique(true);
  };

  return (
    <div>
      <h3 className={"font-medium text-lg mb-2 mt-2"}>
        {t("pages.gallery.generateMediaPanel.generate360Image")}
      </h3>
      {!preview?.thumbnailUrl && (
        <div className="mb-2">
          <div className="font-medium text-gray-700 mt-4">
            {t("pages.gallery.generateMediaPanel.generateFromImageTitle")}
          </div>
          <Checkbox
            label={t("pages.gallery.generateMediaPanel.generateFromImage")}
            registerFct={null}
            checked={img2Img === "img2Img"}
            onChange={toggleImg2Img}
          />
        </div>
      )}
      {img2Img === "img2Img" && (
        <BlockadeImageToImage onSelect={handleImageSelect} onCancel={cancelImageSelection} />
      )}
      {img2Img === "img2Img" && imageSource && (
        <div className="mt-4 mb-4">
          <label
            htmlFor="influence-range"
            className="text-center block mb-2 text-sm font-medium text-black"
          >
            {t("pages.gallery.generateMediaPanel.influenceStrength")}:{" "}
            {Math.round(influenceValue * 100)}%
          </label>
          <div className="flex justify-center">
            <input
              id="influence-range"
              type="range"
              min="0.01"
              max="1"
              value={influenceValue}
              onChange={(event) => setInfluenceValue(Number(event.target.value))}
              step="0.01"
              className="w-96 h-5 bg-gray-200 rounded-lg range-selector cursor-pointer"
              style={{
                accentColor: "rgb(0 180 123)",
              }}
              disabled={isGenerating}
            />
          </div>
          <p className="text-xs text-gray-600 mt-2 text-center">
            {t("pages.gallery.generateMediaPanel.influenceDescription")}
          </p>
        </div>
      )}
      {!preview?.thumbnailUrl && (
        <div>
          <IsLoading
            componentId={FETCH_AI_MODELS_FROM_BLOCKADE_LABS}
            spinnerPlaceholder
            showSuccess={false}
          >
            <Dropdown
              options={models.map((model) => ({ value: String(model.id), optionText: model.name }))}
              asDefaultValue
              defaultValue={defaultModel.id}
              defaultValueIndex={models.findIndex((m) => m.id === 112)}
              label={t("pages.gallery.generateMediaPanel.selectAModel")}
              labelFontClassName={"font-medium"}
              value={selectedModel.id}
              onChangeCb={handleModelChange}
              className="mb-4"
              noStatsAvailable={isGenerating}
            />
            {selectedModel && selectedModel?.id !== 0 && (
              <>
                <div className="font-medium text-gray-700 mt-4">
                  <p>{t("pages.gallery.generateMediaPanel.stylePreview")}</p>
                  <img src={selectedModel.image} alt={selectedModel.name} className="my-2" />
                </div>

                <MediaGenerationInfoParagraph
                  text={`${t("pages.gallery.generateMediaPanel.promptDisclaimerPt1")} ${
                    selectedModel["max-char"]
                  } ${t("pages.gallery.generateMediaPanel.promptDisclaimerPt2")}${
                    img2Img === "exNihilo"
                      ? ""
                      : " " + t("pages.gallery.generateMediaPanel.promptDisclaimerPt3ImgToImg")
                  }`}
                />

                <div className="font-medium text-gray-700 mt-4">
                  <p>{t("pages.gallery.generateMediaPanel.writeYourPrompt")}</p>
                  <TextArea
                    className="mt-4"
                    placeholder={t("pages.gallery.generateMediaPanel.writeYourPrompt")}
                    maxLength={selectedModel["max-char"]}
                    value={prompt}
                    onChange={handlePromptChange}
                    disabled={isGenerating}
                  />
                  <div
                    style={{
                      color: prompt.length >= selectedModel["max-char"] ? "red" : "gray",
                      marginTop: "0.5rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    {prompt.length}/{selectedModel["max-char"]}
                  </div>
                </div>
              </>
            )}
          </IsLoading>
        </div>
      )}
      {!hasGenerateRequestBeenSent ? (
        <button
          disabled={
            !selectedModel || !prompt || isGenerating || (img2Img === "img2Img" && !imageSource)
          }
          className={`btn-primary-fill mr-3 mt-2 ${
            selectedModel.id === 0 || prompt.length === 0 ? "cursor-not-allowed" : ""
          }`}
          onClick={generatePreview}
        >
          {t("general.generate")}
        </button>
      ) : preview?.thumbnailUrl ? (
        <div className="mt-4">
          <form onSubmit={handleSubmit(saveImage)}>
            <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
            <Input
              className="mb-4"
              name="name"
              type="text"
              maxLength={30}
              placeholder={t("general.fileName")}
              autoFocus
              required
              registerFct={() => register("name", { required: "Name is required" })}
            />
            <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
              {t("general.mediaNameAlreadyUsed")}
            </div>

            <div>
              <p className="font-medium text-gray-700 mt-4">
                {t("pages.gallery.generateMediaPanel.yourGeneratedImage")}
              </p>
              <img src={preview.thumbnailUrl} />
            </div>

            <button className={"btn-primary-fill mr-3 mt-2"} type="submit" disabled={isSaving}>
              {t("general.save")}
            </button>
            <button
              className={"btn-primary-outline mr-3 mt-2"}
              onClick={cancelPreview}
              type="button"
            >
              {t("general.cancel")}
            </button>
          </form>
        </div>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
